/**
 * A Linux desktop, drawn.
 *
 * Shared by both Linux practicals, because they are both standing on
 * the same machine and it would be odd if it looked like two.
 *
 * Drawn rather than screenshotted, for the reason the Windows desktops
 * are: a screenshot dates the moment a distribution changes its
 * wallpaper, and there is no single Linux desktop to screenshot anyway.
 * What has to be right is the shape - a bar across the top with
 * Activities at one end and the system menu at the other, a dock of
 * pinned programs down the left, and a Files window whose address reads
 * as a path the learner has just been taught to read.
 */

const SIDEBAR = ['Home', 'Documents', 'Downloads', 'Pictures', 'Other Locations'];

const FILES = [
  { icon: 'doc', name: 'homework.odt', date: '12 Sep', size: '18 kB' },
  { icon: 'img', name: 'birthday.jpg', date: '09 Sep', size: '2.1 MB' },
  { icon: 'folder', name: 'school-project', date: '04 Sep', size: '' },
  { icon: 'pdf', name: 'reading-list.pdf', date: '01 Sep', size: '340 kB' },
  { icon: 'doc', name: 'story-draft.odt', date: '28 Aug', size: '24 kB' },
];

/** The small pictograms in the file list. */
function FileGlyph({ kind, x, y }) {
  if (kind === 'folder') {
    return (
      <g transform={`translate(${x} ${y})`}>
        <path className="lx-g-folder" d="M0,0.6 h1.8 l0.5,0.7 h2.4 v3.1 h-4.7 z" />
      </g>
    );
  }
  if (kind === 'img') {
    return (
      <g transform={`translate(${x} ${y})`}>
        <rect className="lx-g-img" x="0" y="0.6" width="4.6" height="3.8" rx="0.4" />
        <circle className="lx-g-img-sun" cx="1.4" cy="1.8" r="0.5" />
        <path className="lx-g-img-hill" d="M0.3,4.2 L1.8,2.5 L2.9,3.5 L3.8,2.8 L4.3,4.2 Z" />
      </g>
    );
  }
  const tone = kind === 'pdf' ? 'lx-g-pdf' : 'lx-g-doc';
  return (
    <g transform={`translate(${x} ${y})`}>
      <path className={tone} d="M0.4,0.5 h2.7 l1.3,1.3 v2.7 h-4 z" />
      <path className="lx-g-fold" d="M3.1,0.5 v1.3 h1.3 z" />
    </g>
  );
}

/** One pinned program in the dock. */
function DockIcon({ kind, y, running }) {
  return (
    <g transform={`translate(3 ${y})`}>
      {running && <circle className="lx-run-dot" cx="-0.9" cy="4" r="0.55" />}
      {kind === 'files' && (
        <>
          <rect className="lx-g-tile is-files" x="0" y="0" width="8" height="8" rx="1.4" />
          <path className="lx-g-folder" d="M1.6,2.4 h2 l0.6,0.8 h2.2 v3.2 h-4.8 z" />
        </>
      )}
      {kind === 'web' && (
        <>
          <rect className="lx-g-tile is-web" x="0" y="0" width="8" height="8" rx="1.4" />
          <circle className="lx-g-web-ring" cx="4" cy="4" r="2.4" />
          <path className="lx-g-web-arc" d="M1.7,3.2 h4.6 M1.7,4.8 h4.6 M4,1.6 a3.6,3.6 0 0 1 0,4.8 a3.6,3.6 0 0 1 0,-4.8" />
        </>
      )}
      {kind === 'term' && (
        <>
          <rect className="lx-g-tile is-term" x="0" y="0" width="8" height="8" rx="1.4" />
          <rect className="lx-g-term-bar" x="0" y="0" width="8" height="1.5" rx="1.4" />
          <text className="lx-g-term-text" x="1.6" y="6">{'>_'}</text>
        </>
      )}
      {kind === 'shop' && (
        <>
          <rect className="lx-g-tile is-shop" x="0" y="0" width="8" height="8" rx="1.4" />
          <path className="lx-g-shop-bag" d="M2.2,3 h3.6 v3.4 h-3.6 z M3.2,3 a0.8,0.8 0 0 1 1.6,0" />
        </>
      )}
      {kind === 'trash' && (
        <>
          <path className="lx-g-trash" d="M2.4,2.6 h3.2 l-0.4,4.4 h-2.4 z" />
          <rect className="lx-g-trash" x="1.9" y="1.8" width="4.2" height="0.7" rx="0.3" />
        </>
      )}
    </g>
  );
}

/**
 * `showFiles` draws a Files window over the wallpaper; `running` puts
 * the little dot beside the Terminal icon that says it is open.
 */
export default function LinuxDesktopArt({ showFiles = false, running = false }) {
  return (
    <svg viewBox="0 0 160 100" aria-hidden="true">
      <defs>
        <linearGradient id="lx-wall" x1="0" y1="0" x2="0.5" y2="1">
          <stop offset="0%" stopColor="#3b2140" />
          <stop offset="55%" stopColor="#57254a" />
          <stop offset="100%" stopColor="#8a3c36" />
        </linearGradient>
        <radialGradient id="lx-bloom" cx="0.66" cy="0.3" r="0.6">
          <stop offset="0%" stopColor="#ff9a5a" stopOpacity="0.32" />
          <stop offset="100%" stopColor="#ff9a5a" stopOpacity="0" />
        </radialGradient>
      </defs>

      <rect x="0" y="0" width="160" height="100" fill="url(#lx-wall)" />
      <ellipse cx="104" cy="30" rx="86" ry="52" fill="url(#lx-bloom)" />

      {/* ---------- top bar ---------- */}
      <rect className="lx-topbar" x="0" y="0" width="160" height="6" />
      <text className="lx-topbar-text" x="4" y="4.1">Activities</text>
      <text className="lx-topbar-text is-clock" x="80" y="4.1">Mon 14:32</text>

      {/* system menu: network, volume, power */}
      <path className="lx-tray-wifi" d="M143,4 a2.8,2.8 0 0 1 3.8,0 M144,4.9 a1.4,1.4 0 0 1 1.8,0" />
      <path className="lx-tray-speaker" d="M149.4,2.6 h0.9 l1.2,-1 v3.8 l-1.2,-1 h-0.9 z" />
      <path className="lx-tray-power" d="M154.8,1.8 v1.6 M153.5,2.2 a1.9,1.9 0 1 0 2.6,0" />
      <path className="lx-tray-chev" d="M157.6,3.4 l0.9,1 l0.9,-1" />

      {/* ---------- dock ---------- */}
      <rect className="lx-dock" x="1.4" y="9" width="11.2" height="61" rx="2" />
      <DockIcon kind="files" y={11} />
      <DockIcon kind="web" y={21} />
      <DockIcon kind="term" y={31} running={running} />
      <DockIcon kind="shop" y={41} />
      <line className="lx-dock-rule" x1="3" y1="51.4" x2="11" y2="51.4" />
      <DockIcon kind="trash" y={53} />

      {/* ---------- Files ---------- */}
      {showFiles && (
        <g>
          <rect className="lx-win" x="30" y="14" width="112" height="74" rx="2" />

          {/* headerbar - GNOME puts the controls on the right and the
              location in the middle, as breadcrumbs rather than text */}
          <rect className="lx-head" x="30" y="14" width="112" height="7.4" rx="2" />
          <rect className="lx-head" x="30" y="19" width="112" height="2.4" />
          <text className="lx-nav-arrow" x="33.6" y="19.2">←</text>
          <text className="lx-nav-arrow is-dim" x="38.6" y="19.2">→</text>
          <rect className="lx-crumb" x="44" y="15.8" width="12" height="4.2" rx="1" />
          <text className="lx-crumb-text" x="46" y="18.9">aarav</text>
          <rect className="lx-crumb is-sel" x="57" y="15.8" width="18" height="4.2" rx="1" />
          <text className="lx-crumb-text is-sel" x="59" y="18.9">Documents</text>
          <circle className="lx-head-dot" cx="132" cy="17.8" r="1.5" />
          <circle className="lx-head-dot" cx="136" cy="17.8" r="1.5" />
          <circle className="lx-head-dot is-close" cx="140" cy="17.8" r="1.5" />

          {/* sidebar */}
          <rect className="lx-side" x="30" y="21.4" width="30" height="66.6" />
          {SIDEBAR.map((item, i) => (
            <g key={item}>
              {item === 'Documents' && (
                <rect className="lx-side-sel" x="30.6" y={23.2 + i * 6.6} width="28.8" height="5.4" rx="1" />
              )}
              <path className="lx-g-folder-small" d={`M33,${24.6 + i * 6.6} h1.5 l0.4,0.5 h2 v2.2 h-3.9 z`} />
              <text className={`lx-side-text ${item === 'Documents' ? 'is-sel' : ''}`} x="38.4" y={27.4 + i * 6.6}>
                {item}
              </text>
            </g>
          ))}

          {/* column headings */}
          <rect className="lx-cols" x="60" y="21.4" width="82" height="5.4" />
          <text className="lx-col-text" x="66" y="25.2">Name</text>
          <text className="lx-col-text" x="108" y="25.2">Modified</text>
          <text className="lx-col-text" x="130" y="25.2">Size</text>

          {/* files */}
          {FILES.map((f, i) => {
            const y = 27 + i * 7.6;
            return (
              <g key={f.name}>
                {i === 0 && <rect className="lx-row-sel" x="60.6" y={y + 0.5} width="80.8" height="6.6" rx="1" />}
                <FileGlyph kind={f.icon} x={62.4} y={y + 1} />
                <text className="lx-file-name" x="68.4" y={y + 4.9}>{f.name}</text>
                <text className="lx-file-meta" x="108" y={y + 4.9}>{f.date}</text>
                <text className="lx-file-meta" x="130" y={y + 4.9}>{f.size}</text>
              </g>
            );
          })}

          <text className="lx-status-text" x="62" y="85.4">5 items · 1 selected</text>
        </g>
      )}
    </svg>
  );
}
